// Avisos de derivación: personas del courier que reciben un WhatsApp cuando la
// IA deriva un caso de su área. Contrato `settings.handoff_contacts` de
// GET/PUT /api/v1/helpdesk/ai-settings.
//
// Puro: sin React, sin fetch, sin i18n (los mensajes son CLAVES que traduce la
// vista). Las reglas son espejo del DTO del backend (HandoffContactDto): el
// cliente avisa antes, pero quien manda es el backend.

// Áreas del contrato, en el orden en que se ofrecen.
export const HANDOFF_AREAS = ["primera_milla", "ultima_milla", "finanzas"];

// Etiqueta y alcance de cada área (claves i18n: el texto en español).
export const HANDOFF_AREA_LABELS = {
  primera_milla: "Primera milla",
  ultima_milla: "Última milla",
  finanzas: "Finanzas",
};

export const HANDOFF_AREA_HELP = {
  primera_milla:
    "Retiros en el comercio: confirmar, agendar, cambiar o cancelar un retiro, retiro no realizado o atrasado, llegada a bodega.",
  ultima_milla:
    "Entregas, reprogramaciones, entregas fallidas, reclamos de destinatarios y todo lo que planteen los repartidores.",
  finanzas: "Facturación, pagos, notas de cobro y tarifas.",
};

// Topes del contrato (AI_MAX_HANDOFF_CONTACTS / AI_HANDOFF_CONTACT_NAME_MAX).
export const HANDOFF_CONTACTS_MAX = 20;
export const HANDOFF_NAME_MAX = 80;
// Mismo patrón que el @Matches del DTO: código de país obligatorio.
export const HANDOFF_PHONE_RE = /^\+?[\d\s-]{8,20}$/;

const digitsOf = (phone) => String(phone ?? "").replace(/\D/g, "");

let rowSeq = 0;
/** Id local de una fila (solo para React: nunca viaja al backend). */
const nextRowId = () => {
  rowSeq += 1;
  return `hc-${rowSeq}`;
};

/**
 * Fila editable vacía. El área por defecto es la primera del contrato.
 * @returns {{id: string, area: string, name: string, phone: string}}
 */
export function emptyHandoffRow() {
  return { id: nextRowId(), area: HANDOFF_AREAS[0], name: "", phone: "" };
}

/**
 * Filas editables a partir de lo que devolvió el GET. El backend guarda el
 * teléfono en dígitos (con código de país): se muestra con "+" para que se lea
 * como internacional; al guardar vuelve a normalizarse a dígitos en el backend.
 * Un elemento que no es objeto se descarta.
 * @param {unknown} list settings.handoff_contacts
 * @returns {Array<{id: string, area: string, name: string, phone: string}>}
 */
export function contactsToRows(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((c) => c && typeof c === "object")
    .map((c) => {
      const phone = typeof c.phone === "string" ? c.phone.trim() : "";
      return {
        id: nextRowId(),
        area: HANDOFF_AREAS.includes(c.area) ? c.area : HANDOFF_AREAS[0],
        name: typeof c.name === "string" ? c.name : "",
        phone: phone && /^\d+$/.test(phone) ? `+${phone}` : phone,
      };
    });
}

/** true cuando la fila no tiene nada escrito (ni nombre ni teléfono). */
const isBlankRow = (row) => !String(row?.name ?? "").trim() && !String(row?.phone ?? "").trim();

/**
 * Valida las filas con las reglas del backend. Las filas totalmente vacías se
 * ignoran (una fila recién agregada y sin llenar no bloquea el guardado).
 * @param {Array<{id: string, area: string, name: string, phone: string}>} rows
 * @returns {{errors: Record<string, {name?: string, phone?: string, area?: string}>, tooMany: boolean}}
 *   errors por id de fila (valores = claves i18n)
 */
export function validateHandoffRows(rows) {
  const errors = {};
  const seen = new Set();
  const filled = (Array.isArray(rows) ? rows : []).filter((r) => !isBlankRow(r));
  filled.forEach((row) => {
    const rowErrors = {};
    if (!HANDOFF_AREAS.includes(row.area)) rowErrors.area = "Elige un área.";
    const name = String(row.name ?? "").trim();
    if (!name) rowErrors.name = "Escribe el nombre del contacto.";
    else if (name.length > HANDOFF_NAME_MAX) rowErrors.name = "El nombre admite hasta 80 caracteres.";
    const phone = String(row.phone ?? "").trim();
    if (!HANDOFF_PHONE_RE.test(phone)) {
      rowErrors.phone = "Escribe el teléfono con código de país, p. ej. +56912345678.";
    } else {
      // El backend descarta en silencio un área+teléfono repetido: se avisa
      // acá para que el usuario no pierda una fila al guardar.
      const key = `${row.area}:${digitsOf(phone)}`;
      if (seen.has(key)) rowErrors.phone = "Ese teléfono ya está en esta área.";
      seen.add(key);
    }
    if (Object.keys(rowErrors).length > 0) errors[row.id] = rowErrors;
  });
  return { errors, tooMany: filled.length > HANDOFF_CONTACTS_MAX };
}

/**
 * Lista del contrato a partir de las filas (sin ids locales ni filas vacías).
 * Nombre y teléfono van recortados; el teléfono conserva el formato tipeado y
 * el backend lo guarda en dígitos.
 * @param {Array<{area: string, name: string, phone: string}>} rows
 * @returns {Array<{area: string, name: string, phone: string}>}
 */
export function rowsToContacts(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => !isBlankRow(r))
    .map((r) => ({
      area: r.area,
      name: String(r.name ?? "").trim(),
      phone: String(r.phone ?? "").trim(),
    }));
}

/**
 * Firma comparable de una lista (teléfono en dígitos): sirve para saber si hay
 * cambios sin guardar sin que "+56 9…" contra "569…" cuente como diferencia.
 * @param {Array<{area: string, name: string, phone: string}>} contacts
 * @returns {string}
 */
export function contactsSignature(contacts) {
  return JSON.stringify(
    (Array.isArray(contacts) ? contacts : []).map((c) => [
      c.area,
      String(c.name ?? "").trim(),
      digitsOf(c.phone),
    ]),
  );
}

// Grupo del equipo (opcional): `settings.handoff_group` del mismo contrato. Con
// un grupo, los avisos y las consultas de la IA van a ese grupo de WhatsApp en
// un solo mensaje (mencionando a los contactos del área) en vez de directos.
// Mismas reglas que el DTO: jid de grupo y nombre de hasta 120 caracteres.
export const HANDOFF_GROUP_JID_RE = /^[\d-]+@g\.us$/;
export const HANDOFF_GROUP_NAME_MAX = 120;

/**
 * Grupo del contrato ({jid, name}) a partir de un valor cualquiera, o null si
 * no es un grupo válido. Sin nombre se usa el jid (WhatsApp a veces no lo da).
 * @param {unknown} raw
 * @returns {{jid: string, name: string} | null}
 */
export function normalizeHandoffGroup(raw) {
  if (!raw || typeof raw !== "object") return null;
  const jid = typeof raw.jid === "string" ? raw.jid.trim() : "";
  if (!HANDOFF_GROUP_JID_RE.test(jid)) return null;
  const name = typeof raw.name === "string" ? raw.name.trim() : "";
  return { jid, name: (name || jid).slice(0, HANDOFF_GROUP_NAME_MAX) };
}

/**
 * Opciones del selector: los grupos con actividad reciente (en el orden del
 * backend, sin jids inválidos ni repetidos) más el grupo guardado si no vino en
 * la lista (lista caída o grupo sin actividad reciente). Sin esto, el select
 * controlado mostraría "Sin grupo" y se podría guardar null por accidente.
 * @param {unknown} list respuesta `groups` de /conversations/whatsapp-groups
 * @param {{jid: string, name: string} | null} current grupo guardado
 * @returns {Array<{jid: string, name: string, lastMessageAt: string | null}>}
 */
export function handoffGroupOptions(list, current) {
  const seen = new Set();
  const options = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const group = normalizeHandoffGroup(raw);
    if (!group || seen.has(group.jid)) return;
    seen.add(group.jid);
    const lastMessageAt = typeof raw.last_message_at === "string" ? raw.last_message_at : null;
    options.push({ ...group, lastMessageAt });
  });
  if (current && !seen.has(current.jid)) {
    options.unshift({ ...current, lastMessageAt: null });
  }
  return options;
}
